import { NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import { CargoTypeCodeEnum } from '@utils/constants.ts';

/**
 * The values a new shipment starts with, in one place.
 *
 * These used to be applied invisibly: some by the backend when the form sent nothing, one by a
 * literal in {@link newExpeditionInitialData}. Either way the shipment ended up with a value the
 * user had never been shown. Now they are written into the form instead, so the tag shows what will
 * be saved and the user can change it — and every field listed here is required, so removing one is
 * a validation error rather than an invitation for something else to fill it back in.
 *
 * A merge is the exception: where the merged shipments disagreed, the field stays empty and the user
 * has to decide. See the `skip` argument of {@link applyExpeditionDefaults}.
 *
 * Mode of transport, shipment type (RD-2961), truck type and manipulation are deliberately absent:
 * there is no value worth starting them from, so they stay optional and empty.
 */
export type ExpeditionDefaultField = 'cargoType';

export const EXPEDITION_DEFAULTS: Pick<NewExpeditionForm, ExpeditionDefaultField> = {
  cargoType: { cargoTypeCode: CargoTypeCodeEnum.COMMON_GOODS, adrWeight: null },
};

/** Every field that carries a default, for validation and for the tags that have to mark themselves required. */
export const EXPEDITION_DEFAULT_FIELDS = Object.keys(EXPEDITION_DEFAULTS) as ExpeditionDefaultField[];

const isEmpty = (value: unknown): boolean => {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.values(value).every(entry => entry === null || entry === undefined || entry === '');
  return false;
};

/**
 * Fills in the defaults for whatever the caller has not already answered.
 *
 * `skip` names the fields a merge deliberately left empty because its sources disagreed: filling
 * those would put back exactly the value that reflects none of them.
 */
export const applyExpeditionDefaults = <T extends Partial<NewExpeditionForm>>(values: T, skip: ExpeditionDefaultField[] = []): T => {
  const filled = { ...values };

  EXPEDITION_DEFAULT_FIELDS.forEach(field => {
    if (skip.includes(field)) return;
    if (!isEmpty(filled[field])) return;

    (filled as NewExpeditionForm)[field] = EXPEDITION_DEFAULTS[field] as never;
  });

  return filled;
};
